import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  startAt,
  endAt,
  limit,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  serverTimestamp
} from 'firebase/firestore';
import { Link, useLocation } from 'react-router-dom';
import { db } from '../firebase/config';

// ====== Hook de responsividade simples ======
function useViewport() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return {
    width: w,
    isMobile: w <= 640,
    isTablet: w > 640 && w <= 1024,
    isDesktop: w > 1024,
  };
}

const TODOS_PACOTES = [
  'Reflexão', 'Família', 'Terapia de Casal Passado', 'Terapia de Casal Presente',
  'Terapia de Casal Futuro', 'Relacionamentos', 'Memórias', 'Sobre Você',
  'Sobre Grupo', 'Apimentadas', 'Lúdicas', 'Politicamente incorretas',
  'Para fazer rir', 'Crush'
];

// util
const norm = (s) => String(s || '').trim().toLowerCase();

export default function PacoteManager() {
  // Recebe usuário (e-mail) do Dashboard via navigate(..., { state: { email } })
  const location = useLocation();
  const preEmail = (location?.state?.email ?? '');
  const { isMobile, isTablet } = useViewport();

  // UI/estado
  const [emailUsuario, setEmailUsuario] = useState(preEmail);
  const [sugestoes, setSugestoes] = useState([]);
  const [buscaPacote, setBuscaPacote] = useState('');
  const [pacotesSelecionados, setPacotesSelecionados] = useState([]);
  const [mensagem, setMensagem] = useState('');
  const [tipoMsg, setTipoMsg] = useState('info'); // success | error | info
  const [carregandoSug, setCarregandoSug] = useState(false);
  const [salvando, setSalvando] = useState(false);

  // dados do usuário
  const [userInfo, setUserInfo] = useState(null); // {id, email, purchasedPackages, checkedItems}

  // lista de pacotes filtrada
  const listaFiltrada = useMemo(() => {
    const q = norm(buscaPacote);
    if (!q) return TODOS_PACOTES;
    return TODOS_PACOTES.filter((p) => norm(p).includes(q));
  }, [buscaPacote]);

  const countSelecionados = pacotesSelecionados.length;

  // alternar seleção (UI)
  const togglePacote = (pacote) => {
    setPacotesSelecionados((prev) =>
      prev.includes(pacote) ? prev.filter((p) => p !== pacote) : [...prev, pacote]
    );
  };

  const selecionarTodos = () => setPacotesSelecionados((prev) => {
    // união entre o que já estava selecionado e o filtrado
    const set = new Set([...prev, ...listaFiltrada]);
    return Array.from(set);
  });
  const limparSelecao = () => setPacotesSelecionados([]);

  // carregar info do usuário ao mudar e-mail
  useEffect(() => {
    const v = norm(emailUsuario);
    if (!v) { setUserInfo(null); return; }

    let cancel = false;
    (async () => {
      try {
        const qy = query(collection(db, 'users'), where('email', '==', v));
        const snap = await getDocs(qy);
        if (cancel) return;

        if (snap.empty) {
          setUserInfo(null);
        } else {
          const d = snap.docs[0];
          const data = d.data();
          const purchased = Array.isArray(data?.purchasedPackages) ? data.purchasedPackages : [];
          const checked = typeof data?.checkedItems === 'object' && data?.checkedItems !== null ? data.checkedItems : {};

          setUserInfo({
            id: d.id,
            email: data?.email || v,
            purchasedPackages: purchased,
            checkedItems: checked
          });
        }
      } catch {
        setUserInfo(null);
      }
    })();

    return () => { cancel = true; };
  }, [emailUsuario]);

  // Quando userInfo atualizar, marcar automaticamente os pacotes já comprados
  useEffect(() => {
    if (!userInfo) return;
    // Seleciona visualmente tudo que o usuário já possui
    const comprados = Array.isArray(userInfo.purchasedPackages) ? userInfo.purchasedPackages : [];
    setPacotesSelecionados((prev) => {
      // união entre seleção atual (se houver) e os comprados
      const set = new Set([...(prev || []), ...comprados]);
      return Array.from(set);
    });
  }, [userInfo]);

  // autocomplete de e-mail (prefix + debounce)
  useEffect(() => {
    const v = norm(emailUsuario);
    if (v.length < 2) { setSugestoes([]); return; }

    const t = setTimeout(async () => {
      try {
        setCarregandoSug(true);
        const qy = query(
          collection(db, 'users'),
          orderBy('email'),
          startAt(v),
          endAt(v + '\uf8ff'),
          limit(7)
        );
        const snap = await getDocs(qy);
        const encontrados = snap.docs.map((d) => d.data()?.email).filter(Boolean);
        setSugestoes(encontrados);
      } catch {
        setSugestoes([]);
      } finally {
        setCarregandoSug(false);
      }
    }, 300);

    return () => clearTimeout(t);
  }, [emailUsuario]);

  // salvar pacotes — SEMPRE ADICIONAR (append). Não remove nada do que já existe.
  const salvarPacotes = async () => {
    const email = norm(emailUsuario);
    if (!email) {
      setTipoMsg('error'); setMensagem('Informe o e-mail do usuário.'); return;
    }
    if (countSelecionados === 0) {
      setTipoMsg('error'); setMensagem('Selecione ao menos um pacote.'); return;
    }

    setMensagem(''); setTipoMsg('info'); setSalvando(true);

    try {
      // localizar usuário por e-mail
      const qy = query(collection(db, 'users'), where('email', '==', email));
      const snapshot = await getDocs(qy);

      if (snapshot.empty) {
        setTipoMsg('error'); setMensagem('❌ Nenhum usuário encontrado com esse e-mail.');
        setSalvando(false);
        return;
      }

      const userDoc = snapshot.docs[0];
      const userRef = doc(db, 'users', userDoc.id);
      const atual = userDoc.data() || {};

      const jaComprados = Array.isArray(atual.purchasedPackages) ? atual.purchasedPackages : [];
      const checkedAtual = typeof atual.checkedItems === 'object' && atual.checkedItems !== null ? atual.checkedItems : {};

      // união (sem remover nada)
      const setFinal = new Set([...jaComprados, ...pacotesSelecionados]);
      const purchasedPackages = Array.from(setFinal);

      // garante keys em checkedItems para novos pacotes
      const checkedItems = { ...checkedAtual };
      purchasedPackages.forEach((p) => {
        if (!(p in checkedItems)) checkedItems[p] = false;
      });

      await updateDoc(userRef, {
        purchasedPackages,
        checkedItems,
        updatedAt: serverTimestamp()
      });

      // refresh info na tela
      const fresh = await getDoc(userRef);
      const fdata = fresh.data() || {};
      const purchasedRefresh = Array.isArray(fdata?.purchasedPackages) ? fdata.purchasedPackages : [];
      const checkedRefresh = typeof fdata?.checkedItems === 'object' && fdata?.checkedItems !== null ? fdata.checkedItems : {};

      setUserInfo({
        id: userDoc.id,
        email: fdata?.email || email,
        purchasedPackages: purchasedRefresh,
        checkedItems: checkedRefresh
      });

      // mantém a UI coerente: garantir que o selecionado contenha tudo o que o usuário possui
      setPacotesSelecionados((prev) => {
        const set = new Set([...(prev || []), ...purchasedRefresh]);
        return Array.from(set);
      });

      setTipoMsg('success'); setMensagem('✅ Pacotes adicionados com sucesso!');
    } catch (err) {
      console.error(err);
      setTipoMsg('error'); setMensagem('❌ Erro ao atualizar os pacotes.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div style={{ ...s.wrap, padding: isMobile ? 16 : 20 }}>
      {/* Header */}
      <div
        style={{
          ...s.header,
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? 10 : 0,
        }}
      >
        <div>
          <div style={{ ...s.hTitle, fontSize: isMobile ? 18 : 20 }}>Gerenciar Packs</div>
          <div style={s.hSub}>Somente adiciona pacotes — os existentes são preservados</div>
        </div>

        {/* Botão de voltar pro Dashboard */}
        <Link
          to="/"
          style={{
            ...s.btnBack,
            width: isMobile ? '100%' : 'auto',
            textAlign: 'center',
          }}
        >
          ← Voltar para Dashboard
        </Link>
      </div>

      {/* Bloco usuário */}
      <section style={s.block}>
        <div
          style={{
            ...s.blockHead,
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'baseline',
            gap: isMobile ? 4 : 0
          }}
        >
          <div style={s.blockTitle}>Usuário</div>
          <div style={s.blockHint}>Busque pelo e-mail (prefix search)</div>
        </div>

        <div style={{ position: 'relative' }}>
          <input
            type="email"
            placeholder="ex.: ana@exemplo.com"
            value={emailUsuario}
            onChange={(e) => setEmailUsuario(e.target.value)}
            style={s.input}
            autoComplete="off"
          />
          {sugestoes.length > 0 && (
            <ul style={{ ...s.suggestions, maxHeight: isMobile ? 220 : 180 }}>
              {sugestoes.map((email, i) => (
                <li
                  key={i}
                  onClick={() => { setEmailUsuario(email); setSugestoes([]); }}
                  style={{ ...s.suggestionItem, padding: isMobile ? '12px 14px' : '10px 12px' }}
                >
                  {email}
                </li>
              ))}
            </ul>
          )}
          {carregandoSug && <div style={s.muted}>buscando…</div>}
        </div>

        {userInfo ? (
          <div style={s.userCard}>
            <div><strong>ID:</strong> {userInfo.id}</div>
            <div><strong>E-mail:</strong> {userInfo.email}</div>
            <div>
              <strong>Pacotes atuais:</strong>{' '}
              {userInfo.purchasedPackages.length ? userInfo.purchasedPackages.join(', ') : '—'}
            </div>
          </div>
        ) : (
          norm(emailUsuario) && <div style={s.warn}>Nenhum usuário encontrado para “{norm(emailUsuario)}”.</div>
        )}
      </section>

      {/* Bloco pacotes */}
      <section style={s.block}>
        <div
          style={{
            ...s.blockHead,
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'baseline',
            gap: isMobile ? 4 : 0
          }}
        >
          <div style={s.blockTitle}>Pacotes</div>
          <div style={s.blockHint}>Filtre e selecione os pacotes para adicionar</div>
        </div>

        <div
          style={{
            ...s.controlsRow,
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: isMobile ? 8 : 10
          }}
        >
          <input
            placeholder="Filtrar por nome…"
            value={buscaPacote}
            onChange={(e) => setBuscaPacote(e.target.value)}
            style={{ ...s.input, maxWidth: isMobile ? '100%' : 280 }}
          />

          {/* Sem seletor de modo — apenas adicionar */}
          <div
            style={{
              ...s.rightControls,
              flexDirection: isMobile ? 'column' : 'row',
              width: isMobile ? '100%' : 'auto'
            }}
          >
            <button onClick={selecionarTodos} style={{ ...s.btnLight, width: isMobile ? '100%' : 'auto' }}>
              Selecionar todos
            </button>
            <button onClick={limparSelecao} style={{ ...s.btnLight, width: isMobile ? '100%' : 'auto' }}>
              Limpar
            </button>
          </div>
        </div>

        <div style={{ ...s.counter, marginTop: isMobile ? 6 : 8 }}>
          Selecionados: <strong>{countSelecionados}</strong>
        </div>

        <div
          style={{
            ...s.grid,
            gridTemplateColumns: isMobile
              ? '1fr'
              : isTablet
                ? 'repeat(2, minmax(0, 1fr))'
                : 'repeat(3, minmax(0, 1fr))',
            gap: isMobile ? 8 : 10
          }}
        >
          {listaFiltrada.map((pacote) => {
            const checked = pacotesSelecionados.includes(pacote);
            const jaPossui = userInfo?.purchasedPackages?.includes(pacote);

            return (
              <label
                key={pacote}
                title={jaPossui ? 'Usuário já possui este pacote' : undefined}
                style={{
                  ...s.card,
                  padding: '12px',
                  background: checked ? '#eaf1ff' : '#f9f9fb',
                  borderColor: checked ? '#1f6feb' : '#e3e6ef',
                  boxShadow: jaPossui ? 'inset 0 0 0 1px #1f6feb22' : 'none'
                }}
              >
                <input
                  type="checkbox"
                  value={pacote}
                  checked={checked}
                  onChange={() => togglePacote(pacote)}
                  style={{ ...s.checkbox, transform: isMobile ? 'scale(1.2)' : 'scale(1.15)' }}
                />
                <span style={{ lineHeight: 1.2 }}>
                  {pacote}{jaPossui ? ' • (já possui)' : ''}
                </span>
              </label>
            );
          })}
          {listaFiltrada.length === 0 && (
            <div style={{ ...s.empty, padding: isMobile ? 14 : 16 }}>
              Nenhum pacote corresponde ao filtro.
            </div>
          )}
        </div>
      </section>

      {/* Ações */}
      <section style={s.actions}>
        <button
          onClick={salvarPacotes}
          disabled={salvando || !userInfo || countSelecionados === 0}
          style={{
            ...s.btnPrimary,
            width: '100%',
            padding: '12px 14px',
          }}
        >
          {salvando ? 'Salvando…' : 'Adicionar aos atuais'}
        </button>
      </section>

      {/* Feedback */}
      {mensagem && (
        <div
          style={{
            ...s.alert,
            ...(tipoMsg === 'success' ? s.alertSuccess : tipoMsg === 'error' ? s.alertError : s.alertInfo)
          }}
        >
          {mensagem}
        </div>
      )}
    </div>
  );
}

/* ===== estilos padronizados ===== */
const s = {
  wrap: {
    maxWidth: 880,
    margin: '0 auto',
    padding: 20,
    fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
    color: '#111',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16,
  },
  hTitle: { fontSize: 20, fontWeight: 800 },
  hSub: { fontSize: 12, color: '#666' },

  // Botão voltar
  btnBack: {
    textDecoration: 'none',
    padding: '10px 14px',
    borderRadius: 10,
    border: '1px solid #d9dbe7',
    background: '#f5f6fb',
    color: '#111',
    fontSize: 14,
    fontWeight: 700,
    transition: '0.2s ease',
  },

  block: {
    background: '#fff',
    border: '1px solid #e8e9ef',
    borderRadius: 14,
    padding: 16,
    marginBottom: 14,
  },
  blockHead: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 },
  blockTitle: { fontWeight: 800 },
  blockHint: { fontSize: 12, color: '#777' },

  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px solid #d9dbe7',
    background: '#fff',
    outline: 'none',
    fontSize: 14,
  },
  suggestions: {
    position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff',
    border: '1px solid #d9dbe7', borderTop: 'none', zIndex: 10, listStyle: 'none',
    padding: 0, margin: 0, maxHeight: 180, overflowY: 'auto', borderRadius: '0 0 10px 10px',
  },
  suggestionItem: {
    padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f2f3f7', fontSize: 14,
  },
  muted: { fontSize: 12, color: '#777', marginTop: 6 },

  userCard: {
    marginTop: 10,
    padding: 12,
    border: '1px solid #eef0f6',
    background: '#fafbff',
    borderRadius: 10,
    display: 'grid',
    gap: 4,
    fontSize: 14,
  },
  warn: {
    marginTop: 10,
    fontSize: 13,
    color: '#9a3c2f',
    background: '#ffe8e6',
    border: '1px solid #ffcbc4',
    padding: '8px 10px',
    borderRadius: 8,
  },

  controlsRow: {
    display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap',
  },
  rightControls: { display: 'flex', gap: 8, alignItems: 'center' },

  counter: { fontSize: 12, color: '#666', marginTop: 8 },

  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginTop: 12,
  },
  card: {
    padding: '12px 12px',
    border: '2px solid',
    borderRadius: 12,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 14,
    transition: '0.15s ease',
  },
  checkbox: { transform: 'scale(1.15)' },
  empty: {
    gridColumn: '1 / -1',
    padding: 16,
    textAlign: 'center',
    color: '#666',
    background: '#fafafa',
    border: '1px dashed #e0e2ea',
    borderRadius: 10,
  },

  actions: { marginTop: 8 },
  btnPrimary: {
    width: '100%',
    padding: '12px 14px',
    background: '#1f6feb',
    color: '#fff',
    fontSize: 16,
    border: '1px solid #1f6feb',
    borderRadius: 10,
    cursor: 'pointer',
    fontWeight: 700,
  },
  btnLight: {
    padding: '10px 12px',
    background: '#f5f6fb',
    color: '#111',
    border: '1px solid #d9dbe7',
    borderRadius: 10,
    cursor: 'pointer',
    fontWeight: 600,
  },

  alert: {
    marginTop: 12, padding: '10px 12px', borderRadius: 10, fontSize: 14, fontWeight: 600,
  },
  alertSuccess: { background: '#e6f7ee', border: '1px solid #b7ebc6', color: '#1a6f3c' },
  alertError: { background: '#ffe8e6', border: '1px solid #ffcbc4', color: '#9a3c2f' },
  alertInfo: { background: '#eef4ff', border: '1px solid #cddcff', color: '#23429b' },
};
